import { LogInIcon, LogOutIcon, UserIcon } from 'lucide-react'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import { useJoinZone, useLeaveZone, usePresence } from '@/api/queries'
import type { ZoneSummary } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * Join / leave a zone as a HUMAN connection (§16, §18).
 *
 * Presence is not decoration: every write in a zone is attributed to a named
 * connection, so `POST /messages` and the channel writes answer **409** until
 * this exists. The console therefore treats "no presence" as a state with an
 * obvious next action rather than as an error.
 */
export function PresencePanel({ zone }: { zone: ZoneSummary }) {
  const presence = usePresence(zone.id)
  const join = useJoinZone()
  const leave = useLeaveZone()
  const [name, setName] = useState('')

  // `reachable`, not `zone_state`: §17 says no zone of a non-ACTIVE account is
  // reachable whatever the zone's own state, and the server has already folded
  // both halves of that rule into this one flag.
  const writable = zone.reachable

  function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    join.mutate(trimmed.length > 0 ? { zoneId: zone.id, name: trimmed } : { zoneId: zone.id })
  }

  if (presence.isLoading) {
    return null
  }

  if (presence.data !== null && presence.data !== undefined) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-[var(--surface)] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <UserIcon className="size-4 text-primary" />
          <p className="text-sm text-foreground">
            You are in <span className="font-mono">{zone.name}</span> as{' '}
            <span className="font-mono font-medium">{presence.data.name}</span>
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={leave.isPending}
          className="text-muted-foreground hover:text-destructive"
          onClick={() => {
            leave.mutate(zone.id)
          }}
        >
          <LogOutIcon className="size-3.5" />
          {leave.isPending ? 'Leaving…' : 'Leave zone'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border px-4 py-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">You are not in this zone yet.</p>
        <p className="max-w-prose text-sm text-pretty text-muted-foreground">
          Joining claims a name for you, exactly like an agent calling{' '}
          <code className="font-mono">connect</code>. Agents can then address you by that name,
          you get your own message queue, and everything you send is attributed to it.
        </p>
      </div>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <Input
          value={name}
          onChange={(event) => {
            setName(event.target.value)
          }}
          placeholder="your name in this zone (optional)"
          className="w-64 font-mono"
          autoComplete="off"
          disabled={!writable || join.isPending}
        />
        <Button type="submit" disabled={!writable || join.isPending}>
          <LogInIcon className="size-4" />
          {join.isPending ? 'Joining…' : 'Join zone'}
        </Button>
      </form>
      {join.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not join</AlertTitle>
          <AlertDescription><ApiErrorText error={join.error} /></AlertDescription>
        </Alert>
      ) : null}
      {leave.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not leave</AlertTitle>
          <AlertDescription>{errorMessage(leave.error)}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

/**
 * The recovery offered when a write came back 409 `presence-required`: §18 makes
 * that mean exactly one thing — "you have no HUMAN presence here" — so it is
 * shown as an offer to join rather than as a raw conflict error.
 *
 * The offer itself can fail: a HUMAN counts against `max_connections_per_zone`,
 * so `POST /presence` answers 409 on a zone at its quota, and the auto-allocated
 * name can collide. Rendering that is not optional — this notice is the only
 * thing on screen, so a silent failure leaves the user clicking a button that
 * will never work with nothing to read.
 */
export function MissingPresenceNotice({
  zone,
  action,
}: {
  zone: ZoneSummary
  action: string
}) {
  const join = useJoinZone()

  return (
    <Alert variant="warning">
      <AlertTitle>Join the zone first.</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {action} is attributed to a named connection, and you do not have one in{' '}
          <span className="font-mono">{zone.name}</span> yet.
        </p>
        <Button
          size="sm"
          disabled={join.isPending}
          onClick={() => {
            join.mutate({ zoneId: zone.id })
          }}
        >
          <LogInIcon className="size-3.5" />
          {join.isPending ? 'Joining…' : 'Join this zone'}
        </Button>
        {join.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not join</AlertTitle>
            <AlertDescription><ApiErrorText error={join.error} /></AlertDescription>
          </Alert>
        ) : null}
      </AlertDescription>
    </Alert>
  )
}
